import React, { createContext, useState, useContext, useEffect } from 'react';
import { adminAPI } from '../services/api';
import { useAuth } from './AuthContext';

// Create permission context
const PermissionContext = createContext();

// Provider component
export const PermissionProvider = ({ children }) => {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Create a ref to the loadPermissions function so we can call it from outside
  const loadPermissionsRef = React.useRef();

  // Define loadPermissions function
  const loadPermissions = React.useCallback(async () => {
    // Don't run if AuthContext is still loading
    if (authLoading) {
      return;
    }

    if (!isAuthenticated() || !user?.id) {
      setPermissions({});
      setLoading(false);
      return;
    }

    // Skip permission loading for external users (customers)
    if (user.user_type === 'external' || user.userType === 'external') {
      setPermissions({});
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await adminAPI.getUserPermissions(user.id);
      const userPermissions = response.data || {};

      setPermissions(userPermissions);
    } catch (err) {
      console.error('PermissionContext: Failed to load permissions:', err.response?.data || err.message);
      setError(err.response?.data?.message || 'Failed to load permissions');
      setPermissions({});
    } finally {
      setLoading(false);
    }
  }, [user?.id, user?.user_type, authLoading]); // Only depend on stable properties

  // Store the loadPermissions function in the ref so it can be called externally
  loadPermissionsRef.current = loadPermissions;

  // Load user permissions when component mounts
  useEffect(() => {
    loadPermissions();
  }, [loadPermissions]);

  // Listen for authentication changes (storage events and custom events)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'token') {
        loadPermissions();
      }
    };

    const handleAuthChange = (e) => {
      if (e.detail?.type === 'login') {
        loadPermissions();
      } else if (e.detail?.type === 'logout') {
        // Clear permissions immediately on logout to prevent stale data
        setPermissions({});
        setLoading(false);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('authChange', handleAuthChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('authChange', handleAuthChange);
    };
  }, [loadPermissions]);

  // Check if user has a specific permission
  const hasPermission = (permissionName) => {
    if (!permissionName) return false;
    return permissions[permissionName] === true;
  };

  // Check if user has any of the specified permissions
  const hasAnyPermission = (permissionNames) => {
    if (!Array.isArray(permissionNames)) return false;
    return permissionNames.some(permission => hasPermission(permission));
  };

  // Check if user has all of the specified permissions
  const hasAllPermissions = (permissionNames) => {
    if (!Array.isArray(permissionNames)) return false;
    return permissionNames.every(permission => hasPermission(permission));
  };

  // Manual refresh function
  const refreshPermissions = React.useCallback(() => {
    return loadPermissions();
  }, [loadPermissions]);

  // Provide permission context values
  const value = {
    permissions,
    loading,
    error,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    refreshPermissions,
    // Expose individual permission checks for common use cases
    // Users CRUD
    canCreateUsers: hasPermission('create_users'),
    canViewUsers: hasPermission('view_users'),
    canEditUsers: hasPermission('edit_users'),
    canDeleteUsers: hasPermission('delete_users'),
    
    // Customers CRUD
    canCreateCustomers: hasPermission('create_customers'),
    canViewCustomers: hasPermission('view_customers'),
    canEditCustomers: hasPermission('edit_customers'),
    canDeleteCustomers: hasPermission('delete_customers'),
    
    // Inventory CRUD
    canCreateInventory: hasPermission('create_inventory'),
    canViewInventory: hasPermission('view_inventory'),
    canEditInventory: hasPermission('edit_inventory'),
    canDeleteInventory: hasPermission('delete_inventory'),
    
    // Requests CRUD
    canCreateRequests: hasPermission('create_requests'),
    canViewRequests: hasPermission('view_requests'),
    canEditRequests: hasPermission('edit_requests'),
    canDeleteRequests: hasPermission('delete_requests'),
    
    // Invoices CRUD
    canCreateInvoices: hasPermission('create_invoices'),
    canViewInvoices: hasPermission('view_invoices'),
    canEditInvoices: hasPermission('edit_invoices'),
    canDeleteInvoices: hasPermission('delete_invoices'),
    
    // Warehouses CRUD (NEW)
    canCreateWarehouses: hasPermission('create_warehouses'),
    canViewWarehouses: hasPermission('view_warehouses'),
    canEditWarehouses: hasPermission('edit_warehouses'),
    canDeleteWarehouses: hasPermission('delete_warehouses'),
    
    // Settings - General
    canViewGeneralSettings: hasPermission('view_general_settings'),
    canEditGeneralSettings: hasPermission('edit_general_settings'),
    
    // Settings - Notifications
    canViewNotifications: hasPermission('view_notifications'),
    canEditNotifications: hasPermission('edit_notifications'),
    canManageEmailTemplates: hasPermission('manage_email_templates'),
    canManageEscalationRules: hasPermission('manage_escalation_rules'),
    
    // Settings - Security
    canViewSecuritySettings: hasPermission('view_security_settings'),
    canEditSecuritySettings: hasPermission('edit_security_settings'),
    
    // Settings - Users & Access
    canManageRoles: hasPermission('manage_roles'),
    canManageTeams: hasPermission('manage_teams'),
    
    // Settings CRUD (Legacy - keep for backward compatibility)
    canCreateSettings: hasPermission('create_settings'),
    canViewSettings: hasPermission('view_settings'),
    canEditSettings: hasPermission('edit_settings'),
    canDeleteSettings: hasPermission('delete_settings'),
    
    // Special operational permissions
    canDashboard: hasPermission('dashboard'),
    canPickPack: hasPermission('pick_pack'),
    canShipProducts: hasPermission('ship_products'),
    canIncomingInventory: hasPermission('incoming_inventory'),
    canKitBuilding: hasPermission('kit_building'),
    
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

// Hook for using the permission context
export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};

// Higher-order component for permission-based rendering
export const withPermissions = (Component, requiredPermissions) => {
  return (props) => {
    const { hasAnyPermission } = usePermissions();
    
    if (!hasAnyPermission(requiredPermissions)) {
      return null;
    }
    
    return <Component {...props} />;
  };
};

// Component for conditional rendering based on permissions
export const PermissionGate = ({ 
  permissions, 
  requireAll = false, 
  fallback = null, 
  children 
}) => {
  const { hasPermission, hasAnyPermission, hasAllPermissions } = usePermissions();
  
  if (!permissions) return children;
  
  // Handle single permission
  if (typeof permissions === 'string') {
    return hasPermission(permissions) ? children : fallback;
  }
  
  // Handle array of permissions
  if (Array.isArray(permissions)) {
    const hasAccess = requireAll 
      ? hasAllPermissions(permissions) 
      : hasAnyPermission(permissions);
    return hasAccess ? children : fallback;
  }
  
  return fallback;
}; 